import { paddingSmall } from "src/styles/spacing";
import { TagsOverlaySizedTag, TagsOverlayTag } from "../../types";
import parseTagsOverlaySizedTags from "../parseTagsOverlaySizedTags";

interface Rect {
	x: number;
	y: number;
	width: number;
	height: number;
}
const calculateRectCollisionArea = (rect0: Rect, rect1: Rect) => {
	const left = Math.max(rect0.x, rect1.x);
	const right = Math.min(rect0.x + rect0.width, rect1.x + rect1.width);
	const bottom = Math.max(rect0.y, rect1.y);
	const top = Math.min(rect0.y + rect0.height, rect1.y + rect1.height);
	const width = Math.max(0, right - left);
	const height = Math.max(0, top - bottom);

	return width * height;
};

const distanceBetweenRectangles = (rect0: Rect, rect1: Rect): number => {
	if (
		rect0.x < rect1.x + rect1.width &&
		rect0.x + rect0.width > rect1.x &&
		rect0.y < rect1.y + rect1.height &&
		rect0.y + rect0.height > rect1.y
	) {
		return 0;
	}

	let dx = Math.max(
		0,
		Math.max(rect0.x, rect1.x) - Math.min(rect0.x + rect0.width, rect1.x + rect1.width),
	);

	let dy = Math.max(
		0,
		Math.max(rect0.y, rect1.y) - Math.min(rect0.y + rect0.height, rect1.y + rect1.height),
	);

	return Math.sqrt(dx * dx + dy * dy);
};

const faceCollisionMargin = 1;
const edgeCollisionMargin = 1;
const labelCollisionMargin = 1;

const tagCollisionMultiplier = 8;
const faceCollisionMultiplier = 0.3;

const distanceMultiplier = 0.1;
const maxDistance = 128;

const generateTagPlacements = (
	inputTags: TagsOverlayTag[],
	photoScreenDim: {
		photoScreenWidth: number;
		photoScreenHeight: number;
	},
): TagsOverlayTag[] => {
	const tags = parseTagsOverlaySizedTags(inputTags, photoScreenDim);
	const { photoScreenWidth: screenWidth, photoScreenHeight: screenHeight } = photoScreenDim;
	// Yeah we're not even gonna try. Our algorithm is too shit to handle this many tags efficiently, and this shouldn't happen
	if (tags.length > 80) return [];

	/**
	 * Larger scores are worse
	 */
	const getCollisionScore = (tag: TagsOverlaySizedTag, position: { x: number; y: number }) => {
		if (position.x < edgeCollisionMargin || position.y < edgeCollisionMargin) {
			return Infinity;
		}
		const tagRight = position.x + tag.labelDim.width;
		const tagBottom = position.y + tag.labelDim.height;
		if (
			tagRight > screenWidth - edgeCollisionMargin ||
			tagBottom > screenHeight - edgeCollisionMargin
		) {
			return Infinity;
		}

		const totalTagCollision = tags.reduce((acc, otherTag) => {
			const faceCollisionArea =
				faceCollisionMultiplier *
				calculateRectCollisionArea(
					{
						x: position.x,
						y: position.y,
						width: tag.labelDim.width,
						height: tag.labelDim.height,
					},
					{
						x: otherTag.face.x - faceCollisionMargin,
						y: otherTag.face.y - faceCollisionMargin,
						width: otherTag.face.width + 2 * faceCollisionMargin,
						height: otherTag.face.height + 2 * faceCollisionMargin,
					},
				);
			// Don't want to grade label collision area if it's the same tag
			if (tag === otherTag) return acc + faceCollisionArea;
			const labelCollisionArea =
				tagCollisionMultiplier *
				(otherTag.labelPos
					? calculateRectCollisionArea(
							{
								x: position.x,
								y: position.y,
								width: tag.labelDim.width,
								height: tag.labelDim.height,
							},
							{
								x: otherTag.labelPos.x - labelCollisionMargin,
								y: otherTag.labelPos.y - labelCollisionMargin,
								width: otherTag.labelDim.width + 2 * labelCollisionMargin,
								height: otherTag.labelDim.height + 2 * labelCollisionMargin,
							},
						)
					: 0);
			return acc + faceCollisionArea + labelCollisionArea;
		}, 0);

		return totalTagCollision;
	};

	const placeTag = (tag: TagsOverlaySizedTag, desperation: "ideal" | "mid" | "bad" | "awful") => {
		const { face, labelDim } = tag;
		const { x, y, width, height } = face;

		// Ideal placement below face
		const posBelowFace = {
			x: x + width / 2 - labelDim.width / 2,
			y: y + height + paddingSmall,
		};
		if (desperation === "ideal") {
			const collisionScore = getCollisionScore(tag, posBelowFace);
			if (collisionScore === 0) {
				tag.labelPos = posBelowFace;
				return true;
			}
			return false;
		}

		// Less ideal but placing above face
		const posAboveFace = {
			x: x + width / 2 - labelDim.width / 2,
			y: y - labelDim.height - paddingSmall,
		};
		if (desperation === "mid") {
			const collisionScore = getCollisionScore(tag, posAboveFace);
			if (collisionScore === 0) {
				tag.labelPos = posAboveFace;
				return true;
			}
			return false;
		}

		// Aaaand this is where it gets ugly
		// Extending in basically every direction and evaluating the best score
		const handleDesperateScore = ({
			minPos,
			maxPos,
			incrementAmount,
			distanceEvaluator,
		}: {
			minPos: number;
			maxPos: number;
			incrementAmount: (pos: number) => number;
			distanceEvaluator: (weightedDistance: number) => number;
		}) => {
			/**
			 * HIGHER SCORE IS BAD
			 */
			const scores: {
				score: number;
				pos: { x: number; y: number };
			}[] = [];
			for (const basePos of [posBelowFace, posAboveFace]) {
				for (const xSign of [-1, 1]) {
					for (let x = minPos; x < maxPos; x += incrementAmount(x)) {
						for (const ySign of [-1, 1]) {
							for (let y = minPos; y < maxPos; y += incrementAmount(y)) {
								const pos = {
									x: basePos.x + x * xSign,
									y: basePos.y + y * ySign,
								};
								const collisionScore = getCollisionScore(tag, pos);
								const distance = distanceBetweenRectangles(
									{
										x: pos.x,
										y: pos.y,
										width: labelDim.width,
										height: labelDim.height,
									},
									{
										x: tag.face.x,
										y: tag.face.y,
										width: tag.face.width,
										height: tag.face.height,
									},
								);

								scores.push({
									// We want the distance away to be a factor
									score:
										collisionScore +
										distanceEvaluator(
											distance +
												// This exists to bias it away from corners
												0.8 * Math.sqrt(x * x + y * y),
										),
									pos,
								});
							}
						}
					}
				}
			}
			scores.sort((a, b) => a.score - b.score);
			const bestScore = scores[0];
			return bestScore;
		};
		if (desperation === "bad") {
			const bestScore = handleDesperateScore({
				minPos: -32,
				maxPos: maxDistance,
				incrementAmount: (pos) => (pos < maxDistance / 2 ? 8 : 16),
				// Turns all negatives in positives (intentional, since we want to penalize tags based on distance from optimal)
				distanceEvaluator: (distance) => distance * distance * distanceMultiplier,
			});
			if (bestScore && bestScore.score !== Infinity) {
				tag.labelPos = bestScore.pos;
				return true;
			}
			return false;
		}
		if (desperation === "awful") {
			const bestScore = handleDesperateScore({
				minPos: maxDistance,
				maxPos: 4 * maxDistance,
				incrementAmount: () => 16,
				distanceEvaluator: (distance) => distance * distanceMultiplier,
			});
			if (bestScore) {
				tag.labelPos = bestScore.pos;
				return true;
			}
			return false;
		}

		return false;
	};

	const pendingTags: Record<string, TagsOverlaySizedTag> = {};
	for (const tag of tags) {
		pendingTags[tag.curKey] = tag;
	}
	for (const desperation of ["ideal", "mid", "bad", "awful"] as const) {
		for (const tag of Object.values(pendingTags)) {
			if (placeTag(tag, desperation)) {
				delete pendingTags[tag.curKey];
			}
		}
		if (Object.keys(pendingTags).length === 0) {
			break;
		}
	}
	return [...tags];
};
export default generateTagPlacements;
